import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useCategoriesStore } from '../stores/categoriesStore';

interface MonitoringSource {
  source_type: string;
  source_id: string;
  source_name: string;
}

interface Category {
  id: number;
  slug: string;
  name: string;
  display_name: string;
  description?: string;
  is_default: boolean;
  is_custom?: boolean; // Added: explicitly returned from API
  is_active?: boolean;
  is_followed?: boolean;
  company_id?: number;
  created_at?: string;
  updated_at?: string;
  monitoring_sources?: MonitoringSource[];
}

interface CreateCategoryData {
  display_name: string;
  description?: string;
  tags?: string[];
  monitoring_sources?: MonitoringSource[];
}

// Query key factory
const categoriesKeys = {
  all: ['categories'] as const,
  lists: () => [...categoriesKeys.all, 'list'] as const,
  list: (filters?: any) => [...categoriesKeys.lists(), filters] as const,
  details: () => [...categoriesKeys.all, 'detail'] as const,
  detail: (id: number) => [...categoriesKeys.details(), id] as const,
};

// Fetch all categories
export const useCategories = () => {
  const queryClient = useQueryClient();
  const { setCategories, setLoading, setError, shouldRefetch, categories: existingCategories, clearCache } = useCategoriesStore();
  
  // Use a ref to store existing categories to avoid infinite loops
  const existingCategoriesRef = React.useRef(existingCategories);
  React.useEffect(() => {
    existingCategoriesRef.current = existingCategories;
  }, [existingCategories]);
  
  const query = useQuery({
    queryKey: categoriesKeys.lists(),
    queryFn: async () => {
      console.log('[useCategories] 🌐 Fetching categories from API...');
      const response = await apiClient.topics.list();
      console.log('[useCategories] ✅ API response received:', response);
      return response;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't auto-refetch on mount - only refetch when explicitly requested
    refetchOnWindowFocus: false, // Don't auto-refetch on window focus - prevents unwanted refetches
    refetchOnReconnect: false, // Don't auto-refetch on reconnect
    retry: 2,
  });

  // Handle success/error/loading states with useEffect (React Query v5 pattern)
  React.useEffect(() => {
    if (query.data) {
      if (!Array.isArray(query.data)) {
        console.warn('[useCategories] API returned non-array (check VITE_API_URL points to Railway).');
        setError('API returned invalid data. Check that the app is using the correct API URL.');
        return;
      }
      console.log('[useCategories] 🔄 Raw API data received:', query.data);
      console.log('[useCategories] 📊 Checking display_name values:', 
        query.data.map((cat: Category) => ({ id: cat.id, slug: cat.slug, display_name: cat.display_name, name: cat.name }))
      );
      
      // Get existing categories from ref to avoid dependency issues
      const currentExisting = existingCategoriesRef.current;
      
      // Format and update the store - preserve existing counts and active state
      // The store's setCategories will filter out deleted categories automatically
      const formattedCategories = query.data.map((cat: Category, index: number) => {
          const catId = cat.slug || `cat-${cat.id}`;
          const existingCategory = currentExisting.find(c => c.id === catId);
          
          const formatted = {
            id: catId,
            dbId: cat.id,
            slug: cat.slug, // Add slug explicitly
            label: cat.display_name || cat.slug, // Use display_name from API
            count: existingCategory?.count || 0, // Preserve existing count
            active: existingCategory?.active || false, // Preserve existing active state (which tab is selected)
            isDismissed: false,
            color: '#4B9F8E',
            description: cat.description || '',
            isCustom: cat.is_custom !== undefined ? cat.is_custom : !cat.is_default, // Use is_custom from API, fallback to !is_default
            is_default: cat.is_default, // Include is_default for frontend filtering
            is_followed: cat.is_followed !== undefined ? cat.is_followed : true, // Use is_followed from DB
            is_active: cat.is_active !== undefined ? cat.is_active : true, // Keep is_active for enabled/disabled state
            monitoring_sources: cat.monitoring_sources || [], // Include monitoring sources
          };
          console.log(`[useCategories] ✅ Formatted category: "${formatted.label}" (from display_name: "${cat.display_name}")`);
          return formatted;
        });
      
      // If no category is active yet, set the first one as active
      if (!formattedCategories.some(cat => cat.active) && formattedCategories.length > 0) {
        formattedCategories[0].active = true;
      }
      
      // Always update categories - setCategories will preserve active state
      // Only update if we have data - don't clear categories if API returns empty
      if (formattedCategories.length > 0 || currentExisting.length === 0) {
        // Only update if we have new data OR if we don't have existing data (initial load)
        console.log('[useCategories] 💾 Updating store with formatted categories:', formattedCategories.map(c => ({ id: c.id, label: c.label })));
        setCategories(formattedCategories);
        setError(null);
      } else {
        console.warn('[useCategories] ⚠️ API returned empty categories array but we have existing categories - preserving existing categories');
        // Don't update if empty and we have existing categories - preserve existing categories
        // This prevents clearing categories when API temporarily returns empty
      }
    }
    
    if (query.error) {
      setError((query.error as any)?.message || 'Failed to fetch categories');
    }
    
    setLoading(query.isLoading);
  }, [query.data, query.error, query.isLoading, setCategories, setError, setLoading]);

  // Expose a manual refetch function that also clears React Query cache
  const manualRefetch = React.useCallback(async () => {
    console.log('[useCategories] 🔄 Manual refetch triggered - invalidating queries...');
    queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
    // Don't clear cache - let the refetch update the store with new data
    // clearCache() would wipe out all categories temporarily
    return query.refetch();
  }, [query, queryClient]);

  return {
    ...query,
    refetch: manualRefetch, // Override with our manual refetch
  };
};

// Create a new category
export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const { addCategory } = useCategoriesStore();
  
  const mutation = useMutation({
    mutationFn: async (data: CreateCategoryData) => {
      const response = await apiClient.topics.create(data);
      return response;
    },
  });

  // Handle success/error with useEffect (React Query v5 pattern)
  React.useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      // Update the store with the real data
      const formattedCategory = {
        id: mutation.data.slug || `cat-${mutation.data.id}`,
        dbId: mutation.data.id,
        slug: mutation.data.slug,
        label: mutation.data.display_name,
        count: 0,
        active: false,
        isDismissed: false,
        color: '#4B9F8E',
        description: mutation.data.description || '',
        isCustom: true,
      };
      
      addCategory(formattedCategory);
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
    }
  }, [mutation.isSuccess, mutation.data, addCategory, queryClient]);

  return mutation;
};

// Update a category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  const { updateCategory } = useCategoriesStore();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateCategoryData> }) => {
      const response = await apiClient.topics.update(id, data);
      return response;
    },
    onSuccess: (data, variables) => {
      
      // Update the store
      const categoryId = data.slug || `cat-${data.id}`;
      updateCategory(categoryId, {
        label: data.display_name,
        description: data.description || '',
      });
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoriesKeys.detail(variables.id) });
    },
    onError: (error) => {
      // Error handled by mutation state
    },
  });
};

// Delete a category (soft delete - sets is_active to FALSE)
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  
  const mutation = useMutation({
    mutationFn: async (dbId: number) => {
      const response = await apiClient.topics.delete(dbId);
      return { dbId, response };
    },
    // Optimistic update: Remove from cache immediately
    onMutate: async (dbId) => {
      console.log('[useDeleteCategory] 🎯 Starting optimistic delete for dbId:', dbId);
      
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: categoriesKeys.lists() });
      
      // Snapshot the previous value
      const previousCategories = queryClient.getQueryData<Category[]>(categoriesKeys.lists());
      console.log('[useDeleteCategory] 📸 Snapshot taken. Previous categories:', previousCategories?.length);
      
      // Optimistically remove the category from cache
      // The cache stores raw API response where id is the database ID
      queryClient.setQueryData<Category[]>(categoriesKeys.lists(), (old) => {
        if (!old) {
          console.log('[useDeleteCategory] ⚠️ No old data in cache');
          return old;
        }
        
        // Filter out the category by matching the database ID
        // The API returns categories with id as the database ID
        const filtered = old.filter((cat) => {
          const matches = cat.id === dbId;
          if (matches) {
            console.log('[useDeleteCategory] 🗑️ Removing category:', cat.id, cat.display_name || cat.slug);
          }
          return !matches;
        });
        
        console.log('[useDeleteCategory] ✅ Optimistically removed category. Before:', old.length, 'After:', filtered.length);
        if (old.length === filtered.length) {
          console.warn('[useDeleteCategory] ⚠️ No category was removed! dbId:', dbId, 'Available IDs:', old.map(c => c.id));
        }
        return filtered;
      });
      
      // Return context with snapshot for potential rollback
      return { previousCategories };
    },
    // On error, rollback to previous state
    onError: (error, dbId, context) => {
      console.error('[useDeleteCategory] ❌ Delete failed, rolling back:', error);
      if (context?.previousCategories) {
        queryClient.setQueryData(categoriesKeys.lists(), context.previousCategories);
      }
    },
    // On success, just log (optimistic update already done)
    onSuccess: (data, dbId) => {
      console.log('[useDeleteCategory] ✅ Delete confirmed by server for dbId:', dbId);
    },
    // Always refetch after error or success to ensure consistency
    onSettled: () => {
      console.log('[useDeleteCategory] 🔄 Settled, invalidating queries');
      queryClient.invalidateQueries({ queryKey: categoriesKeys.lists() });
    },
  });

  return mutation;
};

// Hook to prefetch categories (useful for navigation)
export const usePrefetchCategories = () => {
  const queryClient = useQueryClient();
  
  return async () => {
    await queryClient.prefetchQuery({
      queryKey: categoriesKeys.lists(),
      queryFn: async () => {
        const response = await apiClient.topics.getAll();
        return response;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
};
